import React, { useEffect, useRef, useState } from 'react'

import clsx from 'clsx'

import Drawer from 'components/Drawer'
import GaDivider from 'components/GaDivider'
import Icon from 'components/Icon'
import Link from 'components/Link'
import Typography from 'components/Typography'
import { useEventContext } from 'context/EventContext'

import classes from './TrustDrawer.module.scss'

/** @type {import("./types").default} */
const TrustDrawer = ({ open, handleClose, className, ...props }) => {
  const [showShadow, setShowShadow] = useState(true)
  const drawerRef = useRef(null)
  const { addEvent } = useEventContext()

  const handleScroll = () => {
    const isScrolledToBottom =
      drawerRef.current.scrollHeight - drawerRef.current.scrollTop === drawerRef.current.clientHeight
    setShowShadow(!isScrolledToBottom)
  }

  useEffect(() => {
    const drawerElement = drawerRef.current
    if (drawerElement) {
      drawerElement.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (drawerElement) {
        drawerElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  return (
    <Drawer
      isSeoFriendly
      open={open}
      onClose={handleClose}
      anchor="right"
      paperProps={{ className: classes.paperProps }}
      modalProps={{ className: classes.modal }}
      className={clsx('TrustDrawer', className, { [classes.fade]: showShadow })}
      {...props}
    >
      <div className={clsx(classes.drawer)} ref={drawerRef}>
        <div className={classes.title}>
          <Typography variant="heading-lg">Our commitment</Typography>
          <Icon onClick={handleClose} icon={['fas-times']} size={20} />
        </div>
        <div className={clsx(classes.fadeDivider)} />
        <section className={classes.section}>
          <Typography variant="heading-md" className={classes.title}>
            Independent research methodology
          </Typography>
          <Typography variant="body-sm">
            Our researchers use a mix of verified reviews, independent research, and objective methodologies to bring
            you selection and ranking information you can trust. While we may earn a referral fee when you visit a
            provider through our links or speak to an advisor, this has no influence on our research or methodology.
          </Typography>
          <Typography className={classes.link}>
            <Link
              href="/resources/proprietary-data-research/"
              target="_blank"
              {...addEvent({
                name: 'engagement_facet_click',
                engagementComponent: 'trust-drawer',
                engagementDestination: 'go_to_blog_research_methodology',
                engagementDetails: 'learn_more_research_methodology',
              })}
            >
              Learn more
            </Link>
          </Typography>
        </section>

        <GaDivider className={classes.divider} />

        <section className={classes.section}>
          <Typography variant="heading-md" className={classes.title}>
            Verified user reviews
          </Typography>
          <Typography>
            GetApp maintains a proprietary database of millions of in-depth, verified user reviews across thousands of
            products in hundreds of software categories. Our data scientists apply advanced modeling techniques to
            identify key insights about products based on those reviews. We may also share aggregated ratings and select
            excerpts from those reviews throughout our site.
          </Typography>
          <Typography className={classes.divider}>
            Our human moderators verify that reviewers are real people and that reviews are authentic. They use leading
            tech to analyze text quality and to detect plagiarism and generative AI.
          </Typography>
          <Typography className={classes.link}>
            <Link
              href="/resources/how-we-verify-reviews/"
              target="_blank"
              {...addEvent({
                name: 'engagement_facet_click',
                engagementComponent: 'trust-drawer',
                engagementDestination: 'go_to_blog_verify_reviews',
                engagementDetails: 'learn_more_verify_reviews',
              })}
            >
              Learn more
            </Link>
          </Typography>
        </section>

        <GaDivider className={classes.divider} />

        <section className={classes.section}>
          <Typography variant="heading-md" className={classes.title}>
            How GetApp ensures transparency
          </Typography>
          <Typography>
            GetApp lists all providers across its website—not just those that pay us—so that users can make informed
            purchase decisions. GetApp is free for users. Software providers pay us for sponsored profiles to receive
            web traffic and sales opportunities. Sponsored profiles include a link-out icon that takes users to the
            provider’s website.
          </Typography>

          <Typography className={classes.link}>
            <Link
              href="/resources/how-we-ensure-transparency/"
              target="_blank"
              {...addEvent({
                name: 'engagement_facet_click',
                engagementComponent: 'trust-drawer',
                engagementDestination: 'go_to_blog_transparency',
                engagementDetails: 'learn_more_transparency',
              })}
            >
              Learn more
            </Link>
          </Typography>
        </section>
        <span className={classes.shadow} />
      </div>
    </Drawer>
  )
}

export default TrustDrawer
